/**
 * Plan 19, Task 4.3 — Sky Quake handler (LISTEN-side, D5).
 *
 * trigger() is the public entry point invoked by external callers (future plans).
 * onSkyQuake() is the integration seam for event-log wiring (deferred to post-Plan-19).
 *
 * Tween implementation uses setTimeout chains so vi.useFakeTimers() can drive
 * tests deterministically — no requestAnimationFrame dependency.
 */

import * as THREE from 'three';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface SkyQuakeHandlerDeps {
  readonly scene: THREE.Scene;
  /** Filtered by consumer to supportsSkyQuake === true bridges. */
  readonly bridgeMeshes: readonly THREE.Mesh[];
  /** Optional animation registry. When absent: setTimeout-based tween (test-friendly). */
  readonly registry?: {
    add(tween: { kill(): void }): void;
    remove(tween: { kill(): void }): void;
  };
}

/** LISTEN-side only (D5). trigger() invoked externally; onSkyQuake() is the
 *  integration seam for future event-log wiring. */
export interface SkyQuakeHandle {
  trigger(durationSeconds: number): void;
  onSkyQuake(cb: (durationSeconds: number) => void): () => void;
  dispose(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const BRIDGE_FADE_OUT_SECS = 0.6;
export const BRIDGE_FADE_IN_SECS = 0.6;

/** Tick interval for the setTimeout tween loop (~60fps). */
const TICK_MS = 16;

// ─────────────────────────────────────────────────────────────────────────────
// Implementation
// ─────────────────────────────────────────────────────────────────────────────

export function createSkyQuakeHandler(deps: SkyQuakeHandlerDeps): SkyQuakeHandle {
  const listeners = new Set<(durationSeconds: number) => void>();
  let disposed = false;

  /** Current active tween token — used to signal the tick loop to stop. */
  let activeTweenId: { killed: boolean } | null = null;

  function trigger(durationSeconds: number): void {
    // Kill any previous tween
    if (activeTweenId !== null) {
      activeTweenId.killed = true;
    }

    // Set meshes transparent before starting tween
    for (const mesh of deps.bridgeMeshes) {
      (mesh.material as THREE.Material).transparent = true;
    }

    // Compute phase boundaries
    const startTime = Date.now();
    const fadeOutEnd = startTime + BRIDGE_FADE_OUT_SECS * 1000;
    const holdEnd = fadeOutEnd + durationSeconds * 1000;
    const fadeInEnd = holdEnd + BRIDGE_FADE_IN_SECS * 1000;

    const tweenToken = { killed: false };
    activeTweenId = tweenToken;

    function tick(): void {
      if (tweenToken.killed || disposed) return;

      const now = Date.now();
      let opacity: number;
      let done = false;

      if (now < fadeOutEnd) {
        // Fade out: 1 → 0
        const elapsed = now - startTime;
        const t = elapsed / (BRIDGE_FADE_OUT_SECS * 1000);
        opacity = 1 - Math.min(t, 1);
      } else if (now < holdEnd) {
        // Hold at 0 — snap immediately when crossing the phase boundary
        opacity = 0;
      } else if (now < fadeInEnd) {
        // Fade in: 0 → 1
        const elapsed = now - holdEnd;
        const t = elapsed / (BRIDGE_FADE_IN_SECS * 1000);
        opacity = Math.min(t, 1);
      } else {
        // Done — snap to 1 and stop
        opacity = 1;
        done = true;
      }

      for (const mesh of deps.bridgeMeshes) {
        (mesh.material as THREE.Material).opacity = opacity;
      }

      if (done) {
        if (activeTweenId === tweenToken) {
          activeTweenId = null;
        }
        return;
      }

      setTimeout(tick, TICK_MS);
    }

    // Start tween on next tick
    setTimeout(tick, TICK_MS);

    // Notify all registered callbacks synchronously (after tween is scheduled)
    for (const cb of listeners) {
      cb(durationSeconds);
    }
  }

  function onSkyQuake(cb: (durationSeconds: number) => void): () => void {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill active tween
    if (activeTweenId !== null) {
      activeTweenId.killed = true;
      activeTweenId = null;
    }

    // Clear all listeners
    listeners.clear();
  }

  return { trigger, onSkyQuake, dispose };
}
